/**
 * ConsentEngine, the central state machine for Pritect Beacon.
 *
 * Coordinates: geo detection, storage, script blocking, banner rendering,
 * consent recording, and Google Consent Mode v2.
 */

import { StorageAdapter, type ConsentDecisions } from "./StorageAdapter";
import { GeoDetector, type GeoResult } from "./GeoDetector";
import { ScriptBlocker, type ConsentCategory } from "../blocking/ScriptBlocker";
import { type BlocklistEntry, compileBlocklist, readCachedBlocklist, sanitizeBlocklist, writeCachedBlocklist } from "../blocking/blocklistCache";
import {
  OPTIONAL_CATEGORIES,
  acceptAllDecisions,
  clampDecisions,
  enabledCategories,
  rejectAllDecisions,
  withdrawnCategories,
} from "./decisions";
import { BannerRenderer } from "../ui/BannerRenderer";
import { PreferenceCenter } from "../ui/PreferenceCenter";
import { GoogleConsentMode } from "../gcm/GoogleConsentMode";
import { ConsentClient } from "../api/ConsentClient";
import { CookieListClient } from "../api/CookieListClient";
import { ExperimentClient, type ExperimentVariant } from "../api/ExperimentClient";
import type { BannerConfig } from "../ui/types";
import { PolicyEvaluator } from "./PolicyEvaluator";
import { TcfApi } from "../tcf/TcfApi";
import { VendorListLoader } from "../tcf/VendorListLoader";
import type { VendorList } from "../tcf/TcfTypes";

export interface ConsentEngineOptions {
  domainId: string;
  apiUrl: string; // Supabase project URL
  anonKey: string; // Supabase public anon key
  blocklist?: BlocklistEntry[] | undefined;
  lang?: string;
  /**
   * A ScriptBlocker that is already intercepting, handed over by the loader.
   *
   * The loader runs before every other tag on the page and starts blocking
   * there, because by the time this bundle arrives the parser has moved on.
   * Constructing a second blocker would install a second set of DOM hooks and
   * lose track of everything the first one already rewrote, so the running one
   * is adopted instead. Absent when the bundle is embedded directly, which is
   * what version-pinned snippets do.
   */
  blocker?: ScriptBlocker;
  /**
   * True when denied Google Consent Mode defaults are already on the page,
   * installed by the loader or by the entry point from data-gcm. Every
   * decision then sends a consent update, on every path, whatever the banner
   * config says: defaults with no update leave Consent Mode denied forever.
   */
  gcmDefaultsInstalled?: boolean;
  /**
   * Let the Google Tag Manager container load before consent (Consent Mode
   * "advanced", data-gcm="advanced"). Only used when the engine creates its
   * own blocker; an adopted blocker already carries the loader's choice.
   */
  tagManagerAllowed?: boolean;
}

/**
 * Sentinel banner version for decisions made on the built-in fallback banner.
 * collect-consent stores those records as fallback records with no banner
 * version, so they are never attributed to a banner the visitor did not see.
 */
export const FALLBACK_BANNER_VERSION = 0;

/**
 * How long a withdrawal waits for its record before reloading anyway. The
 * request is sent with keepalive, so it survives the reload either way; the
 * cap only stops a hung network from leaving withdrawn trackers running.
 */
const WITHDRAWAL_RECORD_WAIT_MS = 5000;

export class ConsentEngine {
  private options: ConsentEngineOptions;
  private storage: StorageAdapter;
  private geo: GeoDetector;
  private blocker: ScriptBlocker;
  /** True when the blocker came from the loader and is already intercepting. */
  private blockerAdopted: boolean;
  private gcm: GoogleConsentMode | null = null;
  private client: ConsentClient;
  private experiments: ExperimentClient;
  private activeExperiment: { id: string; variant: ExperimentVariant } | null = null;
  private sessionId: string;
  private evaluator: PolicyEvaluator = new PolicyEvaluator();
  private preferenceCenter: PreferenceCenter | null = null;
  private currentDecisions: ConsentDecisions | null = null;
  private currentConfig: BannerConfig | null = null;
  private currentGeo: GeoResult | null = null;
  private tcfApi: TcfApi | null = null;
  private vendorListLoader: VendorListLoader | null = null;
  private vendorList: VendorList | null = null;
  private cookieList: CookieListClient;

  constructor(options: ConsentEngineOptions) {
    this.options = options;
    this.storage = new StorageAdapter(options.domainId);
    this.geo = new GeoDetector(options.apiUrl);
    this.blockerAdopted = !!options.blocker;
    this.blocker = options.blocker ?? new ScriptBlocker({ tagManagerAllowed: options.tagManagerAllowed === true });
    // Defaults are already installed, so this instance only ever sends updates.
    if (options.gcmDefaultsInstalled) this.gcm = new GoogleConsentMode(true);
    this.client = new ConsentClient(options.apiUrl, options.domainId);
    this.experiments = new ExperimentClient(options.apiUrl, options.anonKey, options.domainId);
    this.cookieList = new CookieListClient(options.apiUrl, options.domainId);
    this.sessionId = StorageAdapter.generateSessionId();
  }

  async init(): Promise<void> {
    // Step 1: Block scripts immediately (before awaiting anything). When the
    // loader handed one over it has been intercepting since before any other
    // tag on the page ran, so re-intercepting would only duplicate DOM hooks.
    // The scan-derived patterns cached from the last page view are applied
    // first and synchronously, so trackers only the scanner knows about are
    // held on this page view too, not just after the network answers. The
    // loader already did this; addPatterns ignores patterns it already has.
    this.blocker.addPatterns(compileBlocklist(sanitizeBlocklist(this.options.blocklist)));
    const cached = readCachedBlocklist(this.options.domainId);
    this.blocker.addPatterns(cached.patterns);
    if (!this.blockerAdopted) this.blocker.intercept();
    if (cached.stale) void this.refreshBlocklist();

    // Step 2: Detect geo & applicable regulation (needed for TCF decision)
    // Coverage is a customer setting. Read it before geographic suppression,
    // in parallel with location detection while the blocker remains engaged.
    const [geoResult, fetched] = await Promise.all([this.geo.detect(), this.fetchBannerConfig()]);

    // Step 2a: Check for existing valid consent
    const existing = this.storage.load();
    const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months
    const isExpired = existing?.timestamp
      ? Date.now() - new Date(existing.timestamp).getTime() > CONSENT_MAX_AGE_MS
      : false;
    if (existing && !isExpired) {
      // A missing published config must not strand a returning visitor with no
      // way back to their choices: honour the stored decisions against the
      // fallback config so the preference center still renders. pb:error keeps
      // the degraded state observable.
      const config = fetched ?? this.fallbackBannerConfig();
      if (!fetched) {
        this.reportConfigurationError("No published banner config available; applying stored consent with the default banner config.");
      }

      // Re-apply consent from storage without showing banner. A stored
      // category the banner no longer offers is dropped, never released.
      this.currentDecisions = clampDecisions(existing.decisions, config);
      this.currentGeo = geoResult;
      this.currentConfig = config;
      // Install TCF only when the customer enabled it and a real CMP id is set,
      // so we never broadcast an invalid (CMP id 0) TC string.
      if (geoResult.regulation === "gdpr" && config.tcf_enabled && (config.tcf_cmp_id ?? 0) > 0) {
        this.initTcf(true, existing.decisions, geoResult, config.tcf_cmp_id as number);
      }
      this.applyDecisions(existing.decisions, config);

      this.ensurePreferenceCenter(config, geoResult);
      return;
    }

    // Only a readable regional policy can permit geographic suppression.
    // A failed config read must not bypass a site's possible global policy.
    if (!geoResult.requiresConsent && fetched && fetched.global_notice !== true) {
      this.currentConfig = fetched;
      this.currentGeo = geoResult;
      this.currentDecisions = acceptAllDecisions(fetched);
      this.applyDecisions(this.currentDecisions, fetched);
      // A site link must still open settings when no initial banner is needed.
      this.ensurePreferenceCenter(fetched, geoResult);
      return;
    }

    // Step 4: Fetch banner config
    let config = fetched;

    if (!config) {
      // Rendering nothing is not a safe failure for a consent tool: it leaves
      // the site with no consent UI at all while trackers stay blocked and no
      // visitor can ever unblock them. Fall back to a conservative default and
      // keep pb:error observable for operators.
      this.reportConfigurationError("No published banner config available; falling back to the default consent banner.");
      config = this.fallbackBannerConfig();
    }

    // Step 3b: The visitor dismissed the banner earlier this session (Escape,
    // close, overlay). Dismissal is not a decision: nothing was persisted, the
    // blocker stays engaged and GCM stays denied, and we just don't re-nag
    // within the session. Consent remains reachable via the preference center.
    if (this.isSoftDismissed()) {
      if (geoResult.regulation === "gdpr" && config.tcf_enabled && (config.tcf_cmp_id ?? 0) > 0) {
        this.initTcf(false, null, geoResult, config.tcf_cmp_id as number);
      }
      this.ensureGcm(config);
      this.currentConfig = config;
      this.currentGeo = geoResult;
      this.ensurePreferenceCenter(config, geoResult);
      return;
    }

    // Step 4-ab: A/B experiment. Assign a sticky variant and merge its config
    // overrides over the base banner config, so the visitor actually sees the
    // variant and the outcome can be measured. Only on the fresh-banner path.
    const experiment = await this.experiments.fetchActive();
    if (experiment) {
      const variant = ExperimentClient.assignVariant(
        this.storage.getVisitorId(),
        experiment.id,
        experiment.traffic_split
      );
      const overrides = (variant === "a" ? experiment.variant_a : experiment.variant_b) as Partial<BannerConfig>;
      config = { ...config, ...overrides };
      this.activeExperiment = { id: experiment.id, variant };
    }

    // Step 4a: Set up TCF only if GDPR applies, the customer enabled TCF, and a
    // real CMP id is configured. Installing it unconditionally with CMP id 0
    // produced invalid TC strings for every GDPR visitor.
    if (geoResult.regulation === "gdpr" && config.tcf_enabled && (config.tcf_cmp_id ?? 0) > 0) {
      this.initTcf(false, null, geoResult, config.tcf_cmp_id as number);
    }

    // Step 5: Set up GCM defaults before showing banner
    this.ensureGcm(config);

    // Step 6: Show banner
    this.currentConfig = config;
    this.currentGeo = geoResult;
    const renderer = new BannerRenderer(
      config,
      geoResult,
      (decisions) => this.handleDecision(decisions, config!),
      this.options.lang || navigator.language.split("-")[0] || "en",
      null,
      null,
      null,
      () => {
        // Dismissal (Escape/close/overlay): record nothing. Remember it for
        // this session only, and keep the preference center reachable.
        this.markSoftDismissed();
        this.ensurePreferenceCenter(config!, geoResult);
      },
      "banner",
      () => this.cookieList.load()
    );
    renderer.show();

    // Record the experiment impression once the banner is on screen.
    if (this.activeExperiment) {
      this.experiments.record(this.activeExperiment.id, this.activeExperiment.variant, "impression");
    }
  }

  /**
   * Fetch the per-domain blocking manifest built from scan results, cache it
   * for the next page view's synchronous start, and merge it into the running
   * blocker, closing the scan-to-block loop. Served by the public, cached
   * consent-blocklist edge function (service-role read, so tracking data is
   * not exposed via an anon PostgREST policy). Best effort.
   *
   * Patterns that arrive here apply to every script inserted from now on. A
   * script already in the page that one of them matches has run, so the
   * blocker labels it as executed and never replays it on consent.
   */
  private async refreshBlocklist(): Promise<void> {
    try {
      const res = await fetch(
        `${this.options.apiUrl}/functions/v1/consent-blocklist?domain_id=${encodeURIComponent(this.options.domainId)}`,
        {
          headers: { "apikey": this.options.anonKey, "Authorization": `Bearer ${this.options.anonKey}` },
          signal: AbortSignal.timeout(3000),
        }
      );
      if (!res.ok) return;
      const body = await res.json() as { patterns?: unknown };
      const entries = sanitizeBlocklist(body.patterns);
      writeCachedBlocklist(this.options.domainId, entries);
      this.blocker.addPatterns(compileBlocklist(entries));
    } catch {
      // Best effort; static and cached patterns remain in force.
    }
  }

  /**
   * Conservative default used when no published config is readable, because a
   * domain was never configured, was never verified, or the read failed.
   *
   * Deny by default: explicit accept required, no close shortcut, GCM and TCF
   * off, and nothing is released until the visitor decides. The visitor still
   * gets a working consent surface instead of a site with no consent UI.
   */
  private fallbackBannerConfig(): BannerConfig {
    return {
      id: "",
      domain_id: this.options.domainId,
      layout: "bottom_bar",
      primary_color: "#6366f1",
      background_color: "#ffffff",
      text_color: "#111827",
      button_border_radius: "8px",
      logo_url: null,
      font_family: null,
      custom_copy: {},
      show_close_button: false,
      require_explicit_accept: true,
      show_powered_by: true,
      default_regulation: "gdpr",
      categories_enabled: ["strictly_necessary", "analytics", "advertising", "functional"],
      gcm_enabled: false,
      gcm_tag_manager_id: null,
      tcf_enabled: false,
      tcf_cmp_id: null,
      vendor_list_version: null,
      privacy_policy_url: null,
      cookie_policy_url: null,
      // Opt-in, and the fallback carries no customer choice.
      show_cookie_list: false,
      // Not a real banner version: see FALLBACK_BANNER_VERSION.
      version: FALLBACK_BANNER_VERSION,
      is_fallback: true,
    };
  }

  private async fetchBannerConfig(): Promise<BannerConfig | null> {
    try {
      const res = await fetch(
        `${this.options.apiUrl}/rest/v1/banner_configs?domain_id=eq.${this.options.domainId}&published_at=not.is.null&select=*`,
        {
          headers: { "Content-Type": "application/json", "apikey": this.options.anonKey },
          signal: AbortSignal.timeout(3000),
        }
      );
      if (res.ok) {
        const configs = await res.json() as BannerConfig[];
        return configs[0] ?? null;
      }
    } catch {
      // Silently fail
    }
    return null;
  }

  /**
   * Persist, apply and record a decision. Resolves once the record has been
   * sent (and, on a withdrawal, once the page reload has been requested).
   *
   * @param withdrawAll treat every optional category as withdrawn, for
   *   revokeConsent(), whatever the prior state was.
   */
  private handleDecision(
    rawDecisions: ConsentDecisions,
    config: BannerConfig,
    options: { withdrawAll?: boolean } = {}
  ): Promise<void> {
    // A category the banner never offered is never recorded as consented.
    const decisions = clampDecisions(rawDecisions, config);
    // Capture the prior state before we overwrite it, to detect a withdrawal
    // (an offered category that was consented and is now refused).
    const prev = this.currentDecisions;
    const withdrawn: ConsentCategory[] = options.withdrawAll
      ? [...OPTIONAL_CATEGORIES]
      : withdrawnCategories(prev, decisions, config);

    // Save to storage
    this.storage.save({
      decisions,
      timestamp: Date.now(),
      bannerVersion: config.version,
      regulation: config.default_regulation,
      sessionId: this.sessionId,
    });

    // Apply decisions (release scripts, update GCM)
    this.applyDecisions(decisions, config);

    // Update TCF API if active
    let tcString: string | undefined;
    if (this.tcfApi) {
      tcString = this.updateTcfConsent(decisions);
    }

    this.currentDecisions = decisions;

    // Record the experiment outcome (only once, for the first decision made on
    // the banner the visitor was shown). Measured against the categories the
    // banner offered, so Accept all on a subset still counts as accept.
    if (this.activeExperiment) {
      const offered = [...enabledCategories(config)];
      const event = offered.every((c) => decisions[c])
        ? "accept"
        : offered.every((c) => !decisions[c])
          ? "reject"
          : "custom_accept";
      this.experiments.record(this.activeExperiment.id, this.activeExperiment.variant, event);
      this.activeExperiment = null;
    }

    // Show preference center if not already shown
    if (!this.preferenceCenter && this.currentConfig && this.currentGeo) {
      this.ensurePreferenceCenter(this.currentConfig, this.currentGeo);
    }

    const isWithdrawal = withdrawn.length > 0;
    const recorded = this.geo.detect().then((geoResult) =>
      this.client.record(this.sessionId, decisions, geoResult, config.version, tcString, {
        fallback: config.is_fallback === true,
        keepalive: isWithdrawal,
      })
    );

    if (!isWithdrawal) return recorded.then(() => undefined, () => undefined);

    // On withdrawal, clear the withdrawn categories' known cookies and reload,
    // so already-executed trackers actually stop this session (GDPR Art. 7(3):
    // withdrawal must be as effective as giving consent). The reload waits for
    // the record to settle: reloading first aborted the POST, and the server
    // went on showing consent the visitor had withdrawn.
    const waited = new Promise<void>((resolve) => setTimeout(resolve, WITHDRAWAL_RECORD_WAIT_MS));
    return Promise.race([recorded.then(() => undefined, () => undefined), waited]).then(() => {
      ConsentEngine.clearCategoryCookies(withdrawn);
      this.reloadPage();
    });
  }

  /**
   * Withdraw every consent: record a refusal of all optional categories,
   * clear their tracker cookies and reload. Backs the public
   * window.PritectBeacon.revokeConsent().
   */
  async revokeConsent(): Promise<void> {
    const config = this.currentConfig ?? (await this.fetchBannerConfig()) ?? this.fallbackBannerConfig();
    if (!this.currentConfig) this.currentConfig = config;
    if (!this.currentGeo) this.currentGeo = await this.geo.detect();
    ConsentEngine.clearLocalState(this.options.domainId);
    await this.handleDecision(rejectAllDecisions(), config, { withdrawAll: true });
  }

  /**
   * Remove Beacon's non-essential local state. With a domain id, only the
   * experiment identifier goes (the refusal just recorded must survive). With
   * none (no engine could start), every pb_ entry and the pb_consent cookie go
   * too, along with the tracker cookies of every category.
   */
  static clearLocalState(domainId: string | null): void {
    try {
      const drop = (store: Storage) => {
        const keys: string[] = [];
        for (let i = 0; i < store.length; i++) {
          const key = store.key(i);
          if (!key) continue;
          if (domainId ? key === `pb_vid_${domainId}` : key.startsWith("pb_")) keys.push(key);
        }
        keys.forEach((k) => store.removeItem(k));
      };
      drop(localStorage);
      drop(sessionStorage);
    } catch {
      // Storage unavailable
    }
    if (!domainId) {
      document.cookie = "pb_consent=; max-age=0; path=/; SameSite=Lax";
      ConsentEngine.clearCategoryCookies([...OPTIONAL_CATEGORIES]);
    }
  }

  /** Overridable in tests: jsdom cannot reload. */
  protected reloadPage(): void {
    window.location.reload();
  }

  /** Best-effort deletion of well-known tracker cookies for withdrawn categories. */
  static clearCategoryCookies(categories: ConsentCategory[]): void {
    const patterns: Record<ConsentCategory, RegExp[]> = {
      analytics: [/^_ga/, /^_gid$/, /^_gat/, /^_gcl_/, /^__utm/, /^_hj/, /^_clck$/, /^_clsk$/, /^mp_/, /^amplitude/, /^ajs_/],
      advertising: [/^_fbp$/, /^_fbc$/, /^fr$/, /^_gcl_au$/, /^IDE$/, /^test_cookie$/, /^MUID$/, /^_uet/, /^personalization_id$/, /^_ttp/, /^_tt_/],
      functional: [/^intercom-/, /^__hstc$/, /^__hssc$/, /^hubspotutk$/, /^drift/],
      social: [/^guest_id/, /^li_/, /^lidc$/, /^bcookie$/, /^_twitter/],
    };
    const host = window.location.hostname;
    const scopes = ["", host, `.${host}`];
    const parts = host.split(".");
    if (parts.length > 2) scopes.push(`.${parts.slice(-2).join(".")}`);

    const names = document.cookie.split(";").map((c) => c.split("=")[0].trim()).filter(Boolean);
    for (const name of names) {
      const matched = categories.some((cat) => patterns[cat].some((re) => re.test(name)));
      if (!matched) continue;
      for (const scope of scopes) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${scope ? `; domain=${scope}` : ""}`;
      }
    }
  }

  private applyDecisions(rawDecisions: ConsentDecisions, config: BannerConfig | null): void {
    const decisions = clampDecisions(rawDecisions, config);
    // Policy-based enforcement: evaluate each category
    const consented = new Set<ConsentCategory>();
    const regulation = config?.default_regulation ?? "gdpr";
    const cats = ["analytics", "advertising", "functional", "social"] as const;
    for (const cat of cats) {
      const result = this.evaluator.evaluate({ decisions, category: cat, regulation });
      if (result.decision === "allow") consented.add(cat);
    }

    // Keep enforcement live: do not stop() after a decision, so trackers
    // injected later (SPA navigation, late tag managers) stay gated against
    // the recorded consent instead of running unblocked.
    this.blocker.release(consented);

    // Update GCM. Sent whenever defaults are on the page, on every path
    // (the no-consent-needed region, the fallback banner, stored consent),
    // because denied defaults with no update deny measurement forever.
    this.ensureGcm(config);
    this.gcm?.update(decisions);

    // Dispatch DOM event for custom integrations
    window.dispatchEvent(new CustomEvent("pb:consent", { detail: decisions }));
  }

  /**
   * Make sure Consent Mode is set up when it should be. Defaults installed by
   * the loader or the entry point always win: config.gcm_enabled is only
   * consulted when nothing is installed yet, and then installs the defaults
   * itself. Never replaces an existing instance, so a fallback config with
   * gcm_enabled false cannot silence updates the page is waiting for.
   *
   * The config path is what makes Consent Mode a dashboard setting rather than
   * a snippet edit: switch it on, publish, and the next page view has it, with
   * no change to the site. Google's tags are held by the blocker until
   * consent, so the defaults can still be put ahead of anything they queued.
   */
  private ensureGcm(config: BannerConfig | null): void {
    if (this.gcm) return;
    if (!config?.gcm_enabled) return;
    this.gcm = new GoogleConsentMode(true);
    this.gcm.setDefaults({ first: true });
  }

  showPreferences(): void {
    if (!this.preferenceCenter && this.currentConfig && this.currentGeo) {
      this.ensurePreferenceCenter(this.currentConfig, this.currentGeo);
    }
    const pc: PreferenceCenter | null = this.preferenceCenter;
    pc?.openPreferences();
  }

  private ensurePreferenceCenter(config: BannerConfig, geoResult: GeoResult): void {
    if (this.preferenceCenter) return;
    this.preferenceCenter = new PreferenceCenter(
      config,
      geoResult,
      () => this.currentDecisions ?? { strictly_necessary: true, analytics: false, advertising: false, functional: false, social: false },
      (newDecisions) => this.handleDecision(newDecisions, config),
      this.options.lang || navigator.language.split("-")[0] || "en",
      this.vendorList,
      () => {
        const stored = this.storage.load();
        return stored ? { timestamp: stored.timestamp, sessionId: stored.sessionId } : null;
      },
      () => this.cookieList.load()
    );
    this.preferenceCenter.show();
  }

  /**
   * Session-scoped "banner dismissed" flag. Deliberately NOT consent storage:
   * no consent record is created, nothing is sent to the backend, and the
   * banner re-prompts in the next browser session.
   */
  private softDismissKey(): string {
    return `pb_dismissed_${this.options.domainId}`;
  }

  private isSoftDismissed(): boolean {
    try {
      return sessionStorage.getItem(this.softDismissKey()) === "1";
    } catch {
      return false;
    }
  }

  private markSoftDismissed(): void {
    try {
      sessionStorage.setItem(this.softDismissKey(), "1");
    } catch {
      // Storage unavailable: worst case the banner re-shows on next page load.
    }
  }

  private reportConfigurationError(message: string): void {
    console.warn(`[Pritect Beacon] ${message}`);
    window.dispatchEvent(new CustomEvent("pb:error", { detail: { message } }));
  }

  // ─── TCF Integration ──────────────────────────────────────────────

  /**
   * Initialize TCF API and vendor list loader.
   * @param hasExistingConsent Whether consent was already stored
   * @param existingDecisions Stored decisions (if any) to push to TCF API
   * @param geo Geo result for publisher country code
   */
  private initTcf(
    hasExistingConsent: boolean,
    existingDecisions: ConsentDecisions | null,
    geo: GeoResult,
    cmpId: number
  ): void {
    this.tcfApi = new TcfApi({
      cmpId,
      cmpVersion: 1,
      gdprApplies: true,
    });
    this.tcfApi.install(hasExistingConsent);

    // Load vendor list in the background (non-blocking)
    this.vendorListLoader = new VendorListLoader();
    this.vendorListLoader.load().then((vl) => {
      this.vendorList = vl;
      // If we already had consent, update TCF with vendor data now
      if (hasExistingConsent && existingDecisions) {
        this.updateTcfConsent(existingDecisions, false);
      }
    });

    // If existing consent, immediately push what we can (without vendor list)
    if (hasExistingConsent && existingDecisions) {
      this.updateTcfConsent(existingDecisions);
    }
  }

  /**
   * Map Pritect consent categories to TCF purposes and update the TCF API.
   * Returns the TC string.
   */
  private updateTcfConsent(decisions: ConsentDecisions, isUserAction: boolean = true): string {
    if (!this.tcfApi) return "";

    const purposeConsents = new Set<number>();
    const purposeLI = new Set<number>();
    const specialFeatures = new Set<number>();

    // Purpose 1 (store/access information on a device) requires consent under
    // ePrivacy Art. 5(3); it is NOT strictly necessary. Grant it only when the
    // user consented to at least one purpose that relies on device storage, so
    // a Reject All produces a TC string that grants nothing.
    if (decisions.analytics || decisions.advertising || decisions.functional || decisions.social) {
      purposeConsents.add(1);
    }

    // Analytics → Purposes 7, 8, 9
    if (decisions.analytics) {
      purposeConsents.add(7);
      purposeConsents.add(8);
      purposeConsents.add(9);
    }

    // Advertising → Purposes 2, 3, 4, 5, 6, 10
    if (decisions.advertising) {
      purposeConsents.add(2);
      purposeConsents.add(3);
      purposeConsents.add(4);
      purposeConsents.add(5);
      purposeConsents.add(6);
      purposeConsents.add(10);
    }

    // Functional → Purpose 1 (already added) + Special Feature 1 (precise geolocation)
    if (decisions.functional) {
      specialFeatures.add(1);
    }

    // Social → Purposes 3, 4, 5
    if (decisions.social) {
      purposeConsents.add(3);
      purposeConsents.add(4);
      purposeConsents.add(5);
    }

    // Determine vendor consents from vendor list
    const vendorConsents = new Set<number>();
    const vendorLI = new Set<number>();

    if (this.vendorList) {
      for (const vendorId of Object.keys(this.vendorList.vendors)) {
        const vendor = this.vendorList.vendors[vendorId];
        if (!vendor) continue;

        // Consent: vendor is consented if ALL its declared purposes are consented
        const allPurposesConsented = vendor.purposes.every((pid: number) => purposeConsents.has(pid));
        if (allPurposesConsented && vendor.purposes.length > 0) {
          vendorConsents.add(vendor.id);
        }

        // Legitimate interest: vendor LI if all its legInt purposes are satisfied
        const allLIPurposes = vendor.legIntPurposes.every((pid: number) =>
          purposeConsents.has(pid) || purposeLI.has(pid)
        );
        if (allLIPurposes && vendor.legIntPurposes.length > 0) {
          vendorLI.add(vendor.id);
        }
      }
    }

    const geo = this.currentGeo;
    const publisherCC = geo?.country?.toUpperCase() || "AA";
    const consentLanguage = (this.options.lang || navigator.language.split("-")[0] || "en").toUpperCase().substring(0, 2);
    const vendorListVersion = this.vendorList?.vendorListVersion ?? 0;

    this.tcfApi.updateConsent({
      purposeConsents,
      purposeLegitimateInterests: purposeLI,
      vendorConsents,
      vendorLegitimateInterests: vendorLI,
      specialFeatureOptins: specialFeatures,
      vendorListVersion,
      consentLanguage,
      publisherCC,
    }, isUserAction);

    return this.tcfApi.getTcString();
  }
}
